import type { components } from "@nu-assist/contracts";

/** C-12: last_sign_in_at added (not yet in generated contracts). */
export type PersonSummary = components["schemas"]["PersonSummary"] & {
  last_sign_in_at?: string | null;
};

/** One structured audit event from CloudWatch Logs or a local log file. */
export interface ActivityEvent {
  ts: string;                   // ISO 8601 timestamp (timezone-aware)
  service: string;              // "nu-assist" | "nu-sync" | "drift"
  event: string;                // e.g. "admin.offboard" | "drift.alert"
  operation_id: string;         // correlates events from a single admin action
  actor_email: string | null;   // admin who triggered the event
  subject_email: string | null; // person acted on
  result: string;               // ok | partial | manual | flagged | verified | —
  details: Record<string, unknown>;
}

/** Cursor-paginated response from GET /admin/activity. */
export interface ActivityPage {
  items: ActivityEvent[];
  next_cursor: string | null;   // pass as before_ts to load more; null = no more pages
}

/** One row from the admin_users allowlist — returned by GET /admin/admins. */
export interface AdminUser {
  email: string;
  granted_by: string;
  granted_at: string;
  is_owner: boolean;
}
export type OperationResult = components["schemas"]["OperationResult"];
export type StepResult = components["schemas"]["StepResult"];
export type OnboardRequest = components["schemas"]["OnboardRequest"];
/** C-5: return_date is optional — omit or pass null for indefinite leave. */
export interface SetOnLeaveRequest {
  return_date: string | null;
}
export type ChangeEmailRequest = components["schemas"]["ChangeEmailRequest"];

export type PersonStatus =
  | "offboarding_pending"
  | "leftover_seat"
  | "needs_onboarding"
  | "alias_mismatch"
  | "not_a_person"
  | "shared_identity"
  | "never_signed_in"
  | "on_leave"
  | "consistent";

/**
 * STATUS_RULE — canonical detection rule and admin action for each status.
 *
 * Priority order (server applies first match):
 *   offboarding_pending > on_leave > never_signed_in > consistent
 *   (leftover_seat / needs_onboarding / alias_mismatch / not_a_person / shared_identity
 *    are derived from cross-system checks performed by Check Now or nightly drift)
 *
 * | Status               | Detection rule                                                    | Admin action                                      |
 * |----------------------|-------------------------------------------------------------------|---------------------------------------------------|
 * | offboarding_pending  | Disabled in Thrive OR suspended in Google, still active in table  | Offboard — one click runs every system            |
 * | leftover_seat        | HubSpot user exists, owner archived, no active row in table       | Remove HubSpot user                               |
 * | needs_onboarding     | Active in Google and Thrive, no row in our table                  | Onboard                                           |
 * | alias_mismatch       | Same Thrive ID, different email between systems                   | Confirm primary address (needs Google aliases)    |
 * | not_a_person         | Address outside firm domains, or a shared mailbox                 | Remove test rows; mark shared mailboxes           |
 * | shared_identity      | Several rows carry the same Thrive AND HubSpot ID                 | Nothing — engineers act as founding partner by design |
 * | never_signed_in      | Active everywhere, no nu-assist login ever                        | Nothing, or a nudge — adoption signal             |
 * | on_leave             | is_active = false AND on_leave_until IS NOT NULL                  | Nothing until return date                         |
 * | consistent           | Every system agrees and the person uses nu-assist                 | Nothing                                           |
 */
export const STATUS_RULE: Record<PersonStatus, { rule: string; action: string }> = {
  offboarding_pending: {
    rule: "Disabled in Thrive or suspended in Google, still active in our table.",
    action: "Offboard. One click runs every system.",
  },
  leftover_seat: {
    rule: "HubSpot user exists, owner archived, no active row.",
    action: "Remove the HubSpot user.",
  },
  needs_onboarding: {
    rule: "Active in Google and Thrive, no row in our table.",
    action: "Onboard.",
  },
  alias_mismatch: {
    rule: "Same Thrive id, different email between systems.",
    action: "Confirm the primary address. Needs Google aliases to resolve alone.",
  },
  not_a_person: {
    rule: "Address outside the firm domains, or a shared mailbox.",
    action: "Remove test rows. Mark shared mailboxes and vendors so they stop appearing.",
  },
  shared_identity: {
    rule: "Several rows carry the same Thrive and HubSpot id.",
    action: "Nothing. Engineers act as the founding partner by design. Shown so it is never a surprise.",
  },
  never_signed_in: {
    rule: "Active everywhere, no nu-assist login.",
    action: "Nothing, or a nudge. Adoption signal, not an access problem.",
  },
  on_leave: {
    rule: "is_active = false AND on_leave_until IS NOT NULL.",
    action: "Nothing until return date.",
  },
  consistent: {
    rule: "Every system agrees and the person uses nu-assist.",
    action: "Nothing.",
  },
};

export const STATUS_LABEL: Record<PersonStatus, string> = {
  offboarding_pending: "Offboarding pending",
  leftover_seat: "Leftover seat",
  needs_onboarding: "Needs onboarding",
  alias_mismatch: "Alias mismatch",
  not_a_person: "Not a person",
  shared_identity: "Shared identity",
  never_signed_in: "Never signed in",
  on_leave: "On leave",
  consistent: "Consistent",
};

export const STATUS_COLOR: Record<PersonStatus, string> = {
  offboarding_pending: "text-red-600",
  leftover_seat: "text-orange-500",
  needs_onboarding: "text-blue-500",
  alias_mismatch: "text-yellow-600",
  not_a_person: "text-text-muted",
  shared_identity: "text-purple-500",
  never_signed_in: "text-yellow-500",
  on_leave: "text-blue-400",
  consistent: "text-green-600",
};
